// PDF生成モジュール
import { currentSelectedTemplate, layoutData, currentPageSize, albumImages, imageTransforms } from "./state.js";
import { getFormTextValues } from "./utils.js";

// mPDFを使用したPDF生成関数
export const generateAlbumPDFWithMPDF = async () => {
  try {
    console.log("[generateAlbumPDFWithMPDF] mPDFによるPDF生成開始");

    // 必要な要素の存在確認
    if (!currentSelectedTemplate) {
      throw new Error("テンプレートが選択されていません。まずテンプレートを選択してください。");
    }

    // フォーム入力値を取得
    const textValues = getFormTextValues();
    console.log("[generateAlbumPDFWithMPDF] テキスト値:", textValues);

    // 画像データを準備
    const images = [];
    for (let i = 0; i < 4; i++) {
      if (albumImages[i] && albumImages[i].src) {
        images[i] = {
          src: albumImages[i].src,
          name: albumImages[i].name || `image_${i + 1}`,
        };
      } else {
        images[i] = null;
      }
    }

    // 画像変換情報を準備
    const transforms = {};
    for (let i = 0; i < 4; i++) {
      transforms[i] = imageTransforms[i] || {
        scale: 1,
        rotation: 0,
        offsetX: 0,
        offsetY: 0,
      };
    }

    // 送信データを準備
    const requestData = {
      templateId: currentSelectedTemplate.templateId,
      pageSize: currentSelectedTemplate.pageSize || currentPageSize,
      texts: {
        studentName: textValues.studentName || "",
        department: textValues.department || "",
        course: textValues.course || "",
        title: textValues.title || "",
        message: textValues.message || "",
      },
      imageTransforms: transforms,
      images: images,
    };

    console.log("[generateAlbumPDFWithMPDF] 送信データ:", requestData);

    // 生成開始の通知
    showPDFGenerationProgress("PDF生成を開始しています...");

    // mPDFエンドポイントにリクエスト送信
    const response = await fetch("/working_pdf_generator.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP Error: ${response.status}`);
    }

    // 進捗更新
    showPDFGenerationProgress("PDFファイルをダウンロードしています...");

    // PDFファイルをダウンロード
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // ファイル名を取得（レスポンスヘッダーから）
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = "album.pdf";
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // ダウンロードリンクを作成
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    // 進捗を隠す
    hidePDFGenerationProgress();

    // 成功メッセージ
    showPDFGenerationSuccess(filename);

    console.log("[generateAlbumPDFWithMPDF] mPDFによるPDF生成完了:", filename);
    return {
      success: true,
      filename: filename,
      method: "mPDF",
    };
  } catch (error) {
    console.error("[generateAlbumPDFWithMPDF] mPDFによるPDF生成エラー:", error);

    // 進捗を隠す
    hidePDFGenerationProgress();

    // エラーメッセージを表示
    showPDFGenerationError(error.message);

    throw error;
  }
};

// TCPDF使用したPDF生成関数
export const generateAlbumPDFWithTCPDF = async () => {
  try {
    console.log("[generateAlbumPDFWithTCPDF] TCPDFによるPDF生成開始");

    // 必要な要素の存在確認
    if (!currentSelectedTemplate) {
      throw new Error("テンプレートが選択されていません。まずテンプレートを選択してください。");
    }

    // フォーム入力値を取得
    const textValues = getFormTextValues();
    console.log("[generateAlbumPDFWithTCPDF] テキスト値:", textValues);

    // 画像データを準備
    const images = [];
    for (let i = 0; i < 4; i++) {
      if (albumImages[i] && albumImages[i].src) {
        images[i] = {
          src: albumImages[i].src,
          name: albumImages[i].name || `image_${i + 1}`,
          transform: imageTransforms[i] || {},
        };
      }
    }

    // 送信データを準備
    const requestData = {
      template: {
        templateId: currentSelectedTemplate.templateId,
        pageSize: currentSelectedTemplate.pageSize || "B5",
        layout: layoutData[currentSelectedTemplate.templateId],
      },
      images: images,
      texts: textValues,
    };

    console.log("[generateAlbumPDFWithTCPDF] 送信データ:", requestData);

    // 生成開始の通知
    showPDFGenerationProgress("PDF生成中...");

    // TCPDFエンドポイントにリクエスト送信
    const response = await fetch("/tcpdf_album_generator.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`PDF生成エラー: ${response.status} - ${errorText}`);
    }

    // PDFファイルをダウンロード
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // ファイル名の生成
    const studentName = textValues.studentName || "卒業アルバム";
    const templateId = currentSelectedTemplate.templateId;
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, "");
    const filename = `${studentName}_${templateId}_${timestamp}.pdf`;

    // ダウンロードリンクを作成
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    // 進捗を隠す
    hidePDFGenerationProgress();

    // 成功メッセージ
    showPDFGenerationSuccess(filename);

    console.log("[generateAlbumPDFWithTCPDF] TCPDF PDF生成完了");
    return {
      success: true,
      filename: filename,
      method: "TCPDF",
    };
  } catch (error) {
    console.error("[generateAlbumPDFWithTCPDF] TCPDF PDF生成エラー:", error);

    // 進捗を隠す
    hidePDFGenerationProgress();

    // エラーメッセージを表示
    showPDFGenerationError(error.message);

    throw error;
  }
};

// 本番用PDF生成関数（working_pdf_generator.phpベース）
export const generateAlbumPDF = async () => {
  try {
    console.log("[generateAlbumPDF] 本番PDF生成開始");

    // 必要な要素の存在確認
    if (!currentSelectedTemplate) {
      throw new Error("テンプレートが選択されていません。まずテンプレートを選択してください。");
    }

    // フォーム入力値を取得
    const textValues = getFormTextValues();
    console.log("[generateAlbumPDF] テキスト値:", textValues);

    // 画像データを準備
    const images = [];
    for (let i = 0; i < 4; i++) {
      if (albumImages[i] && albumImages[i].src) {
        images[i] = {
          src: albumImages[i].src,
          name: albumImages[i].name || `image_${i + 1}`,
        };
      } else {
        images[i] = null;
      }
    }

    // 送信データを準備（generate_pdf.php用の形式）
    const requestData = {
      templateId: currentSelectedTemplate.templateId,
      pageSize: currentSelectedTemplate.pageSize || currentPageSize,
      textValues: {
        studentName: textValues.studentName || "",
        department: textValues.department || "",
        course: textValues.course || "",
        title: textValues.title || "",
        message: textValues.message || "",
      },
      images: images,
    };

    console.log("[generateAlbumPDF] 送信データ:", requestData);

    // 生成開始の通知
    showPDFGenerationProgress("PDF生成を開始しています...");

    // 本番エンドポイントにリクエスト送信
    const response = await fetch("/generate_pdf.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP Error: ${response.status}`);
    }

    // 進捗更新
    showPDFGenerationProgress("PDFファイルをダウンロードしています...");

    // PDFファイルをダウンロード
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // ファイル名を取得（レスポンスヘッダーから）
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = "album.pdf";
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // ダウンロードリンクを作成
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    // 進捗を隠す
    hidePDFGenerationProgress();

    // 成功メッセージ
    showPDFGenerationSuccess(filename);

    console.log("[generateAlbumPDF] 本番PDF生成完了:", filename);
    return {
      success: true,
      filename: filename,
      method: "Production",
    };
  } catch (error) {
    console.error("[generateAlbumPDF] 本番PDF生成エラー:", error);

    // 進捗を隠す
    hidePDFGenerationProgress();

    // エラーメッセージを表示
    showPDFGenerationError(error.message);

    throw error;
  }
};

// PDF生成進捗表示
export const showPDFGenerationProgress = (message) => {
  // 既存の進捗表示を削除
  hidePDFGenerationProgress();

  const progressDiv = document.createElement("div");
  progressDiv.id = "pdfGenerationProgress";
  progressDiv.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 20px 30px;
    border-radius: 8px;
    z-index: 10000;
    font-size: 16px;
    text-align: center;
    min-width: 250px;
  `;

  progressDiv.innerHTML = `
    <div style="margin-bottom: 10px;">
      <div style="width: 30px; height: 30px; border: 3px solid #f3f3f3; border-top: 3px solid #007bff; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto;"></div>
    </div>
    <div>${message}</div>
  `;

  // スピンアニメーションのCSSを追加
  if (!document.getElementById("pdfSpinnerStyle")) {
    const style = document.createElement("style");
    style.id = "pdfSpinnerStyle";
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  document.body.appendChild(progressDiv);
};

// PDF生成進捗非表示
export const hidePDFGenerationProgress = () => {
  const progressDiv = document.getElementById("pdfGenerationProgress");
  if (progressDiv) {
    progressDiv.remove();
  }
};

// PDF生成成功メッセージ
export const showPDFGenerationSuccess = (filename) => {
  const successDiv = document.createElement("div");
  successDiv.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: #28a745;
    color: white;
    padding: 15px 20px;
    border-radius: 8px;
    z-index: 10000;
    font-size: 14px;
    max-width: 300px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  `;

  successDiv.innerHTML = `
    <div style="font-weight: bold; margin-bottom: 5px;">✓ PDF生成完了</div>
    <div style="font-size: 12px; opacity: 0.9;">${filename}</div>
  `;

  document.body.appendChild(successDiv);

  // 5秒後に自動削除
  setTimeout(() => {
    if (successDiv.parentNode) {
      successDiv.remove();
    }
  }, 5000);
};

// PDF生成エラーメッセージ
export const showPDFGenerationError = (errorMessage) => {
  const errorDiv = document.createElement("div");
  errorDiv.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: #dc3545;
    color: white;
    padding: 15px 20px;
    border-radius: 8px;
    z-index: 10000;
    font-size: 14px;
    max-width: 300px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  `;

  errorDiv.innerHTML = `
    <div style="font-weight: bold; margin-bottom: 5px;">✗ PDF生成エラー</div>
    <div style="font-size: 12px; opacity: 0.9;">${errorMessage}</div>
  `;

  document.body.appendChild(errorDiv);

  // 10秒後に自動削除
  setTimeout(() => {
    if (errorDiv.parentNode) {
      errorDiv.remove();
    }
  }, 10000);
};
